import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ReviewPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true });
  const [rating, setRating] = useState(5);
  const [author, setAuthor] = useState("");
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/reviews/order/${token}`)
      .then((r) => setState({ loading: false, ...r.data }))
      .catch(() => setState({ loading: false, valid: false }));
  }, [token]);

  const submit = async () => {
    if (!author.trim() || text.trim().length < 3) {
      toast.error("Please add your name and a short review.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/reviews/submit", { review_token: token, author, rating, text });
      setDone(true);
      toast.success(data.message || "Thank you for your review!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit review");
    } finally {
      setSubmitting(false);
    }
  };

  const Wrap = ({ children }) => (
    <div data-testid="review-page" className="jm-container py-16 max-w-xl">{children}</div>
  );

  if (state.loading)
    return <Wrap><div className="flex items-center gap-2 text-navy/60"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div></Wrap>;

  if (!state.valid)
    return (
      <Wrap>
        <div data-testid="review-invalid" className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
          <AlertCircle className="w-8 h-8 text-amber-600 mx-auto" />
          <p className="mt-2 font-display text-xl text-navy">This review link isn't valid</p>
          <p className="text-ink/70 text-sm mt-1">The link may be incorrect or expired.</p>
          <Link to="/shop" className="inline-block mt-4 font-bold uppercase text-brick">Back to Shop</Link>
        </div>
      </Wrap>
    );

  if (done || state.already_reviewed)
    return (
      <Wrap>
        <div data-testid="review-done" className="rounded-lg border border-green-200 bg-green-50 p-6 text-center">
          <CheckCircle2 className="w-8 h-8 text-green-600 mx-auto" />
          <p className="mt-2 font-display text-xl text-navy">
            {done ? "Thank you for sharing your experience!" : "This order has already been reviewed"}
          </p>
          <p className="text-ink/70 text-sm mt-1">{done ? "Your review will appear after a quick check." : "Thanks for shopping with JohnnyMart!"}</p>
          <Link to="/shop" className="inline-block mt-4 font-bold uppercase text-brick">Continue Shopping</Link>
        </div>
      </Wrap>
    );

  return (
    <Wrap>
      <p className="uppercase tracking-[0.2em] text-brick font-bold text-sm">JohnnyMart</p>
      <h1 className="font-display text-4xl text-navy mt-2">Leave a Review</h1>
      <p className="text-ink/70 mt-2">
        Thank you for shopping with JohnnyMart. After you receive your order, please consider sharing your honest experience.
        {state.eligible && <span className="ml-1 font-semibold text-green-700">You'll be marked a Verified Buyer.</span>}
      </p>

      <div className="mt-6 flex items-center gap-1" data-testid="review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} type="button" data-testid={`review-star-${n}`} onClick={() => setRating(n)} className="p-0.5">
            <Star className={`w-8 h-8 ${n <= rating ? "fill-gold text-gold" : "text-navy/25"}`} />
          </button>
        ))}
      </div>
      <input data-testid="review-author" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="Your name or initials"
        className="mt-5 w-full rounded-md border border-navy/20 px-3 py-3 outline-none focus:border-brick" />
      <textarea data-testid="review-text" rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="How was your item and your JohnnyMart experience? Honest feedback welcome — positive or not."
        className="mt-3 w-full rounded-md border border-navy/20 px-3 py-3 outline-none focus:border-brick resize-none" />
      <button data-testid="review-submit" onClick={submit} disabled={submitting}
        className="mt-4 inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-8 py-3.5 hover:bg-navy transition-colors disabled:opacity-60">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />} Submit Review
      </button>
    </Wrap>
  );
}
